import type * as THREE from 'three/webgpu';

/**
 * 测量几何类别（浮窗度量口径）。
 */
export enum MeasureGeometryKind {
    /** 顶点。 */
    Vertex = 'vertex',
    /** 直线边。 */
    StraightEdge = 'straight-edge',
    /** 圆弧边。 */
    ArcEdge = 'arc-edge',
    /** 整圆边。 */
    FullCircleEdge = 'full-circle-edge',
    /** 有边界平面面（模型拓扑面）。 */
    PlaneFace = 'plane-face',
    /** 无限延伸基准面/参考面。 */
    InfiniteReferencePlane = 'infinite-reference-plane',
    /** 无限延伸基准轴/参考轴。 */
    InfiniteReferenceLine = 'infinite-reference-line',
    /** 基准点/参考点。 */
    ReferencePoint = 'reference-point',
    /** 圆柱/球面等圆曲面。 */
    CircularSurfaceFace = 'circular-surface-face',
    /** 有边界解析曲面（圆锥/圆环/B样条等，仅面积与周长）。 */
    BoundedSurfaceFace = 'bounded-surface-face',
    /** 暂不支持。 */
    Unsupported = 'unsupported',
}

/**
 * 度量查询上下文。
 */
export interface MeasureMetricQueryContext {
    /** 场景对象。 */
    sceneObject: THREE.Object3D;
    /** 权威拓扑命中快照。 */
    topologyPick: ResolvedTopologyPick | null;
    /** BRep 几何描述符。 */
    brepDescriptor: BRepGeometryDescriptor | null;
    /** 边拓扑快照。 */
    edgeSnapshot: TopologySnapshotEdge | null;
    /** 面拓扑快照。 */
    faceSnapshot: TopologySnapshotFace | null;
    /** 拓扑快照。 */
    topologySnapshot?: TopologySnapshot | null;
}

/**
 * 顶点度量结果。
 */
export interface MeasureVertexMetrics {
    /** 世界坐标（毫米）。 */
    worldPoint: TopologyPoint3D;
}

/**
 * 直线边度量结果。
 */
export interface MeasureStraightEdgeMetrics {
    /** 长度（毫米）。 */
    length: number;
}

/**
 * 圆弧边度量结果。
 */
export interface MeasureArcEdgeMetrics {
    /** 半径（毫米）。 */
    radius: number;
    /** 圆心世界坐标（毫米）。 */
    center: TopologyPoint3D;
}

/**
 * 整圆边度量结果。
 */
export interface MeasureFullCircleEdgeMetrics {
    /** 直径（毫米）。 */
    diameter: number;
    /** 圆心世界坐标（毫米）。 */
    center: TopologyPoint3D;
}

/**
 * 平面度量结果。
 */
export interface MeasurePlaneFaceMetrics {
    /** 面积（平方毫米）。 */
    area: number;
    /** 周长（毫米）。 */
    perimeter: number;
}

/**
 * 有边界面域度量结果（面积 + 周长）。
 */
export type MeasureBoundedFaceMetrics = MeasurePlaneFaceMetrics;

/**
 * 圆曲面度量结果。
 */
export interface MeasureCircularSurfaceFaceMetrics {
    /** 半径（毫米）。 */
    radius: number;
    /** 面积（平方毫米）。 */
    area: number;
    /** 周长（面边界总长度，毫米）。 */
    perimeter: number;
}

/**
 * 两点坐标分量差（ΔX / ΔY / ΔZ）及直线距离度量结果。
 */
export interface MeasureDxDyDzResult {
    /** X 方向分量差（第二点.x − 第一点.x，毫米）。 */
    dx: number;
    /** Y 方向分量差（第二点.y − 第一点.y，毫米）。 */
    dy: number;
    /** Z 方向分量差（第二点.z − 第一点.z，毫米）。 */
    dz: number;
    /** 两点间直线距离（欧氏距离，毫米）。 */
    distance: number;
}

/**
 * 度量结果联合类型。
 */
export type MeasureMetricsResult =
    | MeasureVertexMetrics
    | MeasureStraightEdgeMetrics
    | MeasureArcEdgeMetrics
    | MeasureFullCircleEdgeMetrics
    | MeasurePlaneFaceMetrics
    | MeasureBoundedFaceMetrics
    | MeasureCircularSurfaceFaceMetrics
    | null;
